import React, { useEffect, useState } from "react";
import { Button } from "../../../../ui/components/Button";
import { DialogLayout } from "../../../../ui/layouts/DialogLayout";
import { FeatherCopy, FeatherDownload, FeatherSettings } from "@subframe/core";
import {
  ClientS3ApiError,
  ClientS3CredentialDTO,
  downloadClientS3Profile,
  fetchClientS3CredentialByProfile,
  setupClientS3Credentials,
} from "../../data/api/clientS3CredentialsApi";

interface BulkUploadModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const BulkUploadModal: React.FC<BulkUploadModalProps> = ({
  open,
  onOpenChange,
}) => {
  const PROFILE_NAME = "bulk_download";
  const [credential, setCredential] = useState<ClientS3CredentialDTO | null>(
    null
  );
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [downloaded, setDownloaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [needsSetup, setNeedsSetup] = useState(false);
  const [settingUp, setSettingUp] = useState(false);
  const [configured, setConfigured] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setError(null);
    setDownloaded(false);
    setCopiedField(null);
    setNeedsSetup(false);
    setConfigured(false);
    fetchClientS3CredentialByProfile(PROFILE_NAME)
      .then((data) => {
        setCredential(data);
      })
      .catch((err) => {
        if (err instanceof ClientS3ApiError && err.status === 404) {
          // No bulk_download row yet: offer the one-time self-service setup
          // instead of the raw "S3 credentials not found" error.
          setCredential(null);
          setNeedsSetup(true);
        } else {
          setError(err instanceof Error ? err.message : "Failed to load credentials");
        }
      })
      .finally(() => {
        setLoading(false);
      });
  }, [open]);

  const handleSetup = async () => {
    setSettingUp(true);
    setError(null);
    try {
      await setupClientS3Credentials();
      const data = await fetchClientS3CredentialByProfile(PROFILE_NAME);
      setCredential(data);
      setNeedsSetup(false);
      setConfigured(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to set up credentials");
    } finally {
      setSettingUp(false);
    }
  };

  const handleCopy = async (field: string, value?: string | null) => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      setError("Failed to copy to clipboard");
    }
  };

  const handleDownloadProfile = async () => {
    setDownloading(true);
    setError(null);
    try {
      const { blob, fileName } = await downloadClientS3Profile(PROFILE_NAME);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setDownloaded(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to download profile");
    } finally {
      setDownloading(false);
    }
  };

  const renderCredentialRow = (
    label: string,
    value: string | null | undefined,
    fieldKey: string
  ) => (
    <div className="flex w-full items-center justify-between gap-3">
      <div className="flex min-w-0 flex-col gap-1">
        <span className="text-caption font-caption text-subtext-color">
          {label}
        </span>
        <span className="text-body font-body text-default-font font-mono break-all">
          {value || "-"}
        </span>
      </div>
      <Button
        variant="neutral-tertiary"
        size="small"
        icon={<FeatherCopy />}
        onClick={() => handleCopy(fieldKey, value)}
        disabled={!value}
      >
        {copiedField === fieldKey ? "Copied" : "Copy"}
      </Button>
    </div>
  );

  return (
    <DialogLayout open={open} onOpenChange={onOpenChange}>
      <div className="flex w-full max-w-[720px] flex-col items-start gap-6 bg-default-background px-8 py-6">
        <div className="flex w-full flex-col items-start gap-2">
          <span className="text-heading-2 font-heading-2 text-default-font">
            Bulk Upload
          </span>
          <span className="text-body font-body text-subtext-color">
            Follow the steps below to upload multiple invoices or bills using
            Cyberduck.
          </span>
        </div>

        <div className="flex w-full flex-col items-start gap-4">
          <div className="flex w-full flex-col items-start gap-2">
            <span className="text-body-bold font-body-bold text-default-font">
              Step 1: Download Cyberduck
            </span>
            <span className="text-body font-body text-subtext-color">
              <a
                href="https://cyberduck.io"
                target="_blank"
                rel="noreferrer"
                className="text-body font-body text-brand-600 underline hover:text-brand-700"
              >
                Download Cyberduck (https://cyberduck.io)
              </a>
            </span>
            <span className="text-body font-body text-subtext-color">
              Install it on your system.
            </span>
          </div>

          <div className="flex w-full flex-col items-start gap-2">
            <span className="text-body-bold font-body-bold text-default-font">
              Step 2: Download the S3 configuration
            </span>
            <span className="text-body font-body text-subtext-color">
              Download the provided Cyberduck configuration file.
            </span>
            {needsSetup ? (
              <div className="flex w-full flex-col items-start gap-2">
                <div className="flex items-center gap-2">
                  <Button
                    variant="brand-secondary"
                    size="small"
                    icon={<FeatherSettings />}
                    onClick={handleSetup}
                    disabled={settingUp}
                  >
                    {settingUp ? "Configuring..." : "Configure Cyberduck"}
                  </Button>
                </div>
                <span className="text-body font-body text-subtext-color">
                  One-time setup for this organisation.
                </span>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <Button
                  variant="neutral-secondary"
                  size="small"
                  icon={<FeatherDownload />}
                  onClick={handleDownloadProfile}
                  disabled={downloading}
                >
                  {downloading ? "Preparing..." : "Download Profile"}
                </Button>
              </div>
            )}
            {error && (
              <span className="text-body font-body text-error-600">{error}</span>
            )}
            {(downloaded || configured) && (
              <div className="flex w-full flex-col items-start gap-2 rounded-md border border-solid border-neutral-200 bg-neutral-50 px-3 py-2">
                <span className="text-body-bold font-body-bold text-default-font">
                  AWS credentials
                </span>
                {loading && (
                  <span className="text-body font-body text-subtext-color">
                    Loading credentials...
                  </span>
                )}
                {!loading && credential && (
                  <div className="flex w-full flex-col gap-2">
                    {renderCredentialRow(
                      "Access Key ID",
                      credential.access_key_id,
                      "access_key_id"
                    )}
                    {renderCredentialRow(
                      "Secret Access Key",
                      credential.secret_access_key,
                      "secret_access_key"
                    )}
                    {renderCredentialRow(
                      "S3 Bucket",
                      credential.s3_bucket,
                      "s3_bucket"
                    )}
                    {renderCredentialRow(
                      "AWS Region",
                      credential.s3_region,
                      "s3_region"
                    )}
                    {renderCredentialRow(
                      "Root Folder",
                      credential.root_folder || "",
                      "root_folder"
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="flex w-full flex-col items-start gap-2">
            <span className="text-body-bold font-body-bold text-default-font">
              Step 3: Open the configuration in Cyberduck
            </span>
            <span className="text-body font-body text-subtext-color">
              Double-click the downloaded configuration file.
            </span>
            <span className="text-body font-body text-subtext-color">
              Cyberduck will open automatically and set up the connection.
            </span>
          </div>

          <div className="flex w-full flex-col items-start gap-2">
            <span className="text-body-bold font-body-bold text-default-font">
              Step 4: Enter your credentials
            </span>
            <span className="text-body font-body text-subtext-color">
              When prompted, enter the credentials provided by us.
            </span>
            <span className="text-body font-body text-subtext-color">
              These credentials:
            </span>
            <ul className="list-disc pl-5 text-body font-body text-subtext-color">
              <li>Are restricted to your upload bucket only</li>
              <li>Cannot access any other data</li>
            </ul>
          </div>

          <div className="flex w-full flex-col items-start gap-2">
            <span className="text-body-bold font-body-bold text-default-font">
              Step 5: Upload your files
            </span>
            <span className="text-body font-body text-subtext-color">
              Drag and drop your invoices/bills into the Cyberduck window into
              the respective folders.
            </span>
            <span className="text-body font-body text-subtext-color">
              You can upload any number of files.
            </span>
            <span className="text-body font-body text-subtext-color">
              Files will start uploading immediately.
            </span>
          </div>
        </div>

        <div className="flex w-full items-center justify-end">
          <Button variant="neutral-tertiary" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </div>
      </div>
    </DialogLayout>
  );
};

export default BulkUploadModal;
